import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Card,
  FormLayout,
  Select,
  TextField,
  Button,
  Toast,
  Checkbox,
  DatePicker,
  Popover,
  Icon,
  InlineStack,
  BlockStack,
  Text,
  Spinner,
  Tabs,
  DataTable,
  Pagination,
  Badge
} from '@shopify/polaris';
import { CalendarIcon } from '@shopify/polaris-icons';
import { vendorAPI, orderAPI, manualExportAPI } from '../utils/api';

function ManualExport() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  
  // Filter states
  const [startingOrderId, setStartingOrderId] = useState('');
  const [endingOrderId, setEndingOrderId] = useState('');
  const [status, setStatus] = useState('all');
  const [dateRangeFrom, setDateRangeFrom] = useState(null);
  const [dateRangeTo, setDateRangeTo] = useState(null);
  const [lastXDays, setLastXDays] = useState('');
  const [exportOnlyNotExported, setExportOnlyNotExported] = useState(false);
  const [changeStatusAfterExport, setChangeStatusAfterExport] = useState(false);
  const [newStatus, setNewStatus] = useState('in_progress');
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [fromDatePickerActive, setFromDatePickerActive] = useState(false);
  const [toDatePickerActive, setToDatePickerActive] = useState(false);
  const [fromMonth, setFromMonth] = useState(new Date().getMonth());
  const [fromYear, setFromYear] = useState(new Date().getFullYear());
  const [toMonth, setToMonth] = useState(new Date().getMonth());
  const [toYear, setToYear] = useState(new Date().getFullYear());
  
  // Orders List Tab states
  const [activeTab, setActiveTab] = useState(0);
  const [orders, setOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [shopTimezone, setShopTimezone] = useState('America/New_York'); // Default timezone
  const [ordersPagination, setOrdersPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
  });
  const [ordersFilters, setOrdersFilters] = useState({
    fulfillment: 'all',
    financial: 'all'
  });

  // Auto-dismiss error toast after 3 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Auto-dismiss success toast after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fulfillmentStatusOptions = [
    { label: '--- All Statuses ---', value: 'all' },
    { label: 'Fulfilled', value: 'fulfilled' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'On Hold', value: 'on_hold' },
    { label: 'Partial', value: 'partial' },
    { label: 'Restocked', value: 'restocked' },
    { label: 'Scheduled', value: 'scheduled' },
    { label: 'Unfulfilled', value: 'unfulfilled' }
  ];

  const newStatusOptions = [
    { label: 'No Change', value: '' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'On Hold', value: 'on_hold' },
    { label: 'Partial', value: 'partial' },
    { label: 'Fulfilled', value: 'fulfilled' },
    { label: 'Scheduled', value: 'scheduled' }
  ];

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    if (selectedVendor) {
      fetchTemplates(selectedVendor);
    } else {
      setTemplates([]);
      setSelectedTemplate('');
    }
  }, [selectedVendor]);

  // Fetch orders when switching to Orders List tab
  useEffect(() => {
    if (activeTab === 0) {
      fetchOrders();
    }
  }, [activeTab, ordersPagination.page, ordersPagination.limit, searchQuery, ordersFilters]);

  const fetchVendors = async () => {
    try {
      const response = await vendorAPI.getAll();
      setVendors(response.data.vendors || []);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError('Failed to load vendors');
    }
  };

  const fetchTemplates = async (vendorId) => {
    try {
      const response = await vendorAPI.getTemplates(vendorId);
      setTemplates(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedTemplate(response.data[0].id);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
    }
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const params = {
        page: ordersPagination.page,
        limit: ordersPagination.limit,
        ...(searchQuery && { search: searchQuery }),
        ...(ordersFilters.fulfillment !== 'all' && { 
          fulfillmentStatus: ordersFilters.fulfillment 
        }),
        ...(ordersFilters.financial !== 'all' && { 
          financialStatus: ordersFilters.financial 
        })
      };

      const response = await orderAPI.list(params);

      setOrders(response.data.orders || []);
      if (response.data.shopTimezone) {
        setShopTimezone(response.data.shopTimezone);
      }
      setOrdersPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        totalPages: response.data.pagination.totalPages
      }));
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleSelectOrder = (orderId) => {
    setSelectedOrderIds(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      }
      return [...prev, orderId];
    });
  };

  const handleSelectAllOrders = () => {
    if (selectedOrderIds.length === orders.length && orders.length > 0) {
      setSelectedOrderIds([]);
    } else {
      setSelectedOrderIds(orders.map(o => o.id));
    }
  };

  const handleExportSelectedOrders = async () => {
    if (selectedOrderIds.length === 0) {
      setError('Please select at least one order');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await orderAPI.exportSelected({
        vendorId: selectedVendor,
        templateId: selectedTemplate,
        orderIds: selectedOrderIds
      });

      // Download file
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = 'export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(`Successfully exported ${selectedOrderIds.length} order${selectedOrderIds.length !== 1 ? 's' : ''}`);
      setSelectedOrderIds([]);
      
      // Refresh orders list
      fetchOrders();
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.error || 'Export failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedVendor('');
    setSelectedTemplate('');
    setStartingOrderId('');
    setEndingOrderId('');
    setStatus('all');
    setDateRangeFrom(null);
    setDateRangeTo(null);
    setLastXDays('');
    setExportOnlyNotExported(false);
    setChangeStatusAfterExport(false);
    setNewStatus('in_progress');
    setError(null);
    setSuccess(null);
  };

  const handleExport = async () => {
    setError(null);
    setSuccess(null);

    // Validation: Vendor and Template are mandatory
    if (!selectedVendor || !selectedTemplate) {
      setError('Please select a vendor and template');
      return;
    }

    // Validation: At least one filter condition is required
    const hasStartingOrder = startingOrderId && startingOrderId.trim();
    const hasDateRange = dateRangeFrom || dateRangeTo;
    const hasLastXDays = lastXDays && lastXDays.trim();

    if (!hasStartingOrder && !hasDateRange && !hasLastXDays) {
      setError('Please provide at least one filter: Starting Order Number, Date Range, or Last X Days');
      return;
    }

    // Validation: Only one filter type is allowed
    const filterCount = [hasStartingOrder, hasDateRange, hasLastXDays].filter(Boolean).length;
    if (filterCount > 1) {
      setError('Please use only one filter type at a time: Starting Order Number OR Date Range OR Last X Days');
      return;
    }

    setLoading(true);

    try {
      // Helper function to format date as YYYY-MM-DD without timezone conversion
      const formatDateString = (date) => {
        if (!date) return undefined;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const filters = {
        vendorId: selectedVendor,
        templateId: selectedTemplate,
        startingOrderId: startingOrderId || undefined,
        endingOrderId: endingOrderId || undefined,
        status: status !== 'all' ? status : undefined,
        // Send dates as YYYY-MM-DD strings without timezone conversion
        dateRangeFrom: formatDateString(dateRangeFrom),
        dateRangeTo: formatDateString(dateRangeTo),
        lastXDays: lastXDays ? parseInt(lastXDays) : undefined,
        exportOnlyNotExported,
        changeStatusAfterExport,
        newStatus: changeStatusAfterExport ? newStatus : undefined
      };

      const response = await manualExportAPI.export(filters);

      // Get filename from response headers
      const contentDisposition = response.headers?.['content-disposition'];
      let filename = 'export.csv'; // Default to csv
      if (contentDisposition) {
        // Match filename="something.ext" or filename=something.ext
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Fallback: determine extension from selected template format
      if (filename === 'export.csv' && selectedTemplateData) {
        const ext = selectedTemplateData.format === 'xlsx' ? 'xlsx' : 'csv';
        filename = `export.${ext}`;
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess('Export completed successfully!');
    } catch (err) {
      console.error('Export error:', err);
      setError(err.response?.data?.error || 'Failed to export orders');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    
    // Parse the UTC timestamp
    const date = new Date(dateString);
    
    // Get current date/time in shop timezone
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: shopTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    const datePartsInTz = formatter.formatToParts(date);
    const nowPartsInTz = formatter.formatToParts(now);
    
    const dateDay = datePartsInTz.find(p => p.type === 'day').value;
    const dateMonth = datePartsInTz.find(p => p.type === 'month').value;
    const dateYear = datePartsInTz.find(p => p.type === 'year').value;
    
    const nowDay = nowPartsInTz.find(p => p.type === 'day').value;
    const nowMonth = nowPartsInTz.find(p => p.type === 'month').value;
    const nowYear = nowPartsInTz.find(p => p.type === 'year').value;
    
    // Format time in shop timezone (lowercase am/pm to match Shopify)
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: shopTimezone
    }).toLowerCase();
    
    // Check if today
    if (dateDay === nowDay && dateMonth === nowMonth && dateYear === nowYear) {
      return `Today at ${timeStr}`;
    }
    
    // Check if yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayPartsInTz = formatter.formatToParts(yesterday);
    const yesterdayDay = yesterdayPartsInTz.find(p => p.type === 'day').value;
    const yesterdayMonth = yesterdayPartsInTz.find(p => p.type === 'month').value;
    const yesterdayYear = yesterdayPartsInTz.find(p => p.type === 'year').value;
    
    if (dateDay === yesterdayDay && dateMonth === yesterdayMonth && dateYear === yesterdayYear) {
      return `Yesterday at ${timeStr}`;
    }
    
    // Otherwise show full date with time in shop timezone
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: shopTimezone
    }).toLowerCase().replace(/\s([ap]m)/, ' $1');
  };

  const vendorOptions = [
    { label: '--- Select Vendor ---', value: '' },
    ...vendors.map(v => ({
      label: `${v.vendorName} (${v.vendorCode})`,
      value: v.id
    }))
  ];

  const templateOptions = templates.map(t => ({
    label: `${t.templateName} (${t.format.toUpperCase()})`,
    value: t.id
  }));

  const selectedVendorData = vendors.find(v => v.id === selectedVendor);
  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  const tabs = [
    {
      id: 'orders-list',
      content: 'Orders List',
      accessibilityLabel: 'Browse and select orders',
      panelID: 'orders-list-panel'
    },
    {
      id: 'bulk-operation',
      content: 'Bulk Operation',
      accessibilityLabel: 'Bulk operation by criteria',
      panelID: 'bulk-operation-panel'
    }
  ];

  return (
    <Page
      title="Manual Export"
      subtitle="Export orders based on vendor/template and custom filters"
    >
      <BlockStack gap="400">

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Profile</Text>
            
            <FormLayout>
              <BlockStack gap="200">
                <Select
                  label="Vendor / Template"
                  options={vendorOptions}
                  value={selectedVendor}
                  onChange={setSelectedVendor}
                />
                {selectedTemplateData && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {selectedTemplateData.templateName} • {selectedTemplateData.format.toUpperCase()}
                  </Text>
                )}
              </BlockStack>
            </FormLayout>
          </BlockStack>
        </Card>

        <Card>
          <Tabs tabs={tabs} selected={activeTab} onSelect={setActiveTab}>
            <BlockStack gap="400">
              {activeTab === 0 ? (
                // Orders List Tab
                <>
                    <Card>
                      <BlockStack gap="400">
                        <InlineStack gap="400">
                          <div style={{ flex: 2 }}>
                            <TextField
                              label="Search orders"
                              value={searchQuery}
                              onChange={setSearchQuery}
                              placeholder="Search by order number, customer name, or email"
                              autoComplete="off"
                              clearButton
                              onClearButtonClick={() => setSearchQuery('')}
                            />
                          </div>
                          
                          <div style={{ flex: 1 }}>
                            <Select
                              label="Fulfillment Status"
                              options={[
                                { label: 'All', value: 'all' },
                                { label: 'Fulfilled', value: 'fulfilled' },
                                { label: 'Unfulfilled', value: null },
                                { label: 'Partial', value: 'partial' },
                                { label: 'In Progress', value: 'in_progress' }
                              ]}
                              value={ordersFilters.fulfillment}
                              onChange={(value) => setOrdersFilters(prev => ({ 
                                ...prev, 
                                fulfillment: value 
                              }))}
                            />
                          </div>
                          
                          <div style={{ flex: 1 }}>
                            <Select
                              label="Financial Status"
                              options={[
                                { label: 'All', value: 'all' },
                                { label: 'Paid', value: 'paid' },
                                { label: 'Pending', value: 'pending' },
                                { label: 'Refunded', value: 'refunded' },
                                { label: 'Voided', value: 'voided' }
                              ]}
                              value={ordersFilters.financial}
                              onChange={(value) => setOrdersFilters(prev => ({ 
                                ...prev, 
                                financial: value 
                              }))}
                            />
                          </div>
                        </InlineStack>
                      </BlockStack>
                    </Card>

                    <Card>
                      {ordersLoading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                          <Spinner size="large" />
                          <Text variant="bodyMd" tone="subdued">Loading orders...</Text>
                        </div>
                      ) : orders.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                          <Text variant="bodyMd" tone="subdued">No orders found</Text>
                        </div>
                      ) : (
                        <>
                          <DataTable
                            hideScrollIndicator
                            columnContentTypes={[
                              'text',
                              'text',
                              'text',
                              'text',
                              'text',
                              'numeric',
                              'text',
                              'text',
                              'numeric'
                            ]}
                            headings={[
                              <Checkbox
                                checked={selectedOrderIds.length === orders.length && orders.length > 0}
                                onChange={handleSelectAllOrders}
                              />,
                              'Order',
                              'Date',
                              'Customer',
                              'Email',
                              'Total',
                              'Fulfillment',
                              'Payment',
                              'Items'
                            ]}
                            rows={orders.map(order => {
                              const isVoided = order.financial_status === 'voided';
                              const strikeStyle = isVoided ? { textDecoration: 'line-through', opacity: 0.6 } : {};
                              
                              return [
                                <Checkbox
                                  checked={selectedOrderIds.includes(order.id)}
                                  onChange={() => handleSelectOrder(order.id)}
                                />,
                                <span style={strikeStyle}>{order.name || `#${order.order_number}`}</span>,
                                <span style={strikeStyle}>{formatDateTime(order.created_at)}</span>,
                                <span style={strikeStyle}>{order.customer ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim() : '-'}</span>,
                                <span style={strikeStyle}>{order.email || '-'}</span>,
                                <span style={strikeStyle}>{`$${order.total_price}`}</span>,
                                <span style={strikeStyle}>
                                  <Badge status={order.fulfillment_status === 'fulfilled' ? 'success' : order.fulfillment_status === 'partial' ? 'attention' : 'warning'}>
                                    {order.fulfillment_status || 'unfulfilled'}
                                  </Badge>
                                </span>,
                                <span style={strikeStyle}>
                                  <Badge status={order.financial_status === 'paid' ? 'success' : order.financial_status === 'pending' ? 'attention' : 'critical'}>
                                    {order.financial_status}
                                  </Badge>
                                </span>,
                                <span style={strikeStyle}>{order.line_items?.length || 0}</span>
                              ];
                            })}
                          />

                          <div style={{ padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text variant="bodySm" tone="subdued">
                              {selectedOrderIds.length} of {ordersPagination.total} orders selected
                            </Text>
                            
                            <InlineStack gap="300" align="center">
                              <div style={{ minWidth: '120px' }}>
                                <Select
                                  label=""
                                  labelHidden
                                  options={[
                                    { label: '25 per page', value: '25' },
                                    { label: '50 per page', value: '50' },
                                    { label: '100 per page', value: '100' }
                                  ]}
                                  value={ordersPagination.limit.toString()}
                                  onChange={(value) => setOrdersPagination(prev => ({ 
                                    ...prev, 
                                    limit: parseInt(value),
                                    page: 1 // Reset to first page when changing page size
                                  }))}
                                />
                              </div>
                              {ordersPagination.totalPages > 1 && (
                                <Text variant="bodySm" tone="subdued">
                                  Page {ordersPagination.page} of {ordersPagination.totalPages}
                                </Text>
                              )}
                              <Pagination
                                hasPrevious={ordersPagination.page > 1}
                                onPrevious={() => setOrdersPagination(prev => ({ 
                                  ...prev, 
                                  page: prev.page - 1 
                                }))}
                                hasNext={ordersPagination.page < ordersPagination.totalPages}
                                onNext={() => setOrdersPagination(prev => ({ 
                                  ...prev, 
                                  page: prev.page + 1 
                                }))}
                              />
                            </InlineStack>
                          </div>
                        </>
                      )}
                    </Card>

                    <InlineStack align="end" gap="200">
                      <Button
                        onClick={() => {
                          setSearchQuery('');
                          setOrdersFilters({ fulfillment: 'all', financial: 'all' });
                          setSelectedOrderIds([]);
                          setOrdersPagination(prev => ({ ...prev, page: 1, limit: 25 }));
                        }}
                        disabled={ordersLoading}
                      >
                        Reset
                      </Button>
                      <Button
                        primary
                        onClick={handleExportSelectedOrders}
                        loading={loading}
                        disabled={!selectedVendor || !selectedTemplate || selectedOrderIds.length === 0}
                      >
                        Export {selectedOrderIds.length} Selected Order{selectedOrderIds.length !== 1 ? 's' : ''}
                      </Button>
                    </InlineStack>
                  </>
                ) : (
                  // Filters Tab
                  <>
                    <Card>
                      <BlockStack gap="400">
                        <Text variant="headingMd" as="h2">Order ID Range</Text>
            
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Starting Order Number"
                  value={startingOrderId}
                  onChange={setStartingOrderId}
                  placeholder="403800"
                  helpText="Enter order number (e.g., 403800), not Shopify ID"
                  autoComplete="off"
                />
                
                <TextField
                  label="Ending Order Number"
                  value={endingOrderId}
                  onChange={setEndingOrderId}
                  placeholder="403850"
                  helpText="Leave empty to export until the latest order"
                  autoComplete="off"
                />
              </FormLayout.Group>
            </FormLayout>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="400">
            <Text variant="headingMd" as="h2">Additional Filters</Text>
            
            <FormLayout>
              <Select
                label="Fulfillment Status"
                options={fulfillmentStatusOptions}
                value={status}
                onChange={setStatus}
              />

              <FormLayout.Group>
                <div>
                  <Text as="p" variant="bodyMd" fontWeight="medium">Date Range From</Text>
                  <Popover
                    active={fromDatePickerActive}
                    activator={
                      <Button
                        onClick={() => setFromDatePickerActive(!fromDatePickerActive)}
                        disclosure
                        icon={CalendarIcon}
                      >
                        {formatDate(dateRangeFrom) || 'Select date'}
                      </Button>
                    }
                    onClose={() => setFromDatePickerActive(false)}
                  >
                    <Card>
                      <DatePicker
                        month={fromMonth}
                        year={fromYear}
                        onChange={({ start }) => {
                          setDateRangeFrom(start);
                          setFromDatePickerActive(false);
                        }}
                        onMonthChange={(month, year) => {
                          setFromMonth(month);
                          setFromYear(year);
                        }}
                        selected={dateRangeFrom}
                      />
                    </Card>
                  </Popover>
                </div>

                <div>
                  <Text as="p" variant="bodyMd" fontWeight="medium">Date Range To</Text>
                  <Popover
                    active={toDatePickerActive}
                    activator={
                      <Button
                        onClick={() => setToDatePickerActive(!toDatePickerActive)}
                        disclosure
                        icon={CalendarIcon}
                      >
                        {formatDate(dateRangeTo) || 'Select date'}
                      </Button>
                    }
                    onClose={() => setToDatePickerActive(false)}
                  >
                    <Card>
                      <DatePicker
                        month={toMonth}
                        year={toYear}
                        onChange={({ start }) => {
                          setDateRangeTo(start);
                          setToDatePickerActive(false);
                        }}
                        onMonthChange={(month, year) => {
                          setToMonth(month);
                          setToYear(year);
                        }}
                        selected={dateRangeTo}
                      />
                    </Card>
                  </Popover>
                </div>
              </FormLayout.Group>

              <TextField
                label="Created during the last X days"
                value={lastXDays}
                onChange={setLastXDays}
                type="number"
                placeholder="e.g., 30"
                autoComplete="off"
              />
            </FormLayout>
          </BlockStack>
        </Card>

                    <InlineStack align="end" gap="200">
                      <Button
                        onClick={handleReset}
                        disabled={loading}
                      >
                        Reset
                      </Button>
                      <Button
                        primary
                        onClick={handleExport}
                        loading={loading}
                        disabled={!selectedVendor || !selectedTemplate}
                      >
                        {loading ? 'Exporting...' : 'Export'}
                      </Button>
                    </InlineStack>
                  </>
                )}
              </BlockStack>
            </Tabs>
          </Card>
      </BlockStack>

      {error && (
        <Toast
          content={error}
          error
          onDismiss={() => setError(null)}
        />
      )}

      {success && (
        <Toast
          content={success}
          onDismiss={() => setSuccess(null)}
        />
      )}
    </Page>
  );
}

export default ManualExport;
